import { BodyText, Button, Flex } from '@utilitywarehouse/hearth-react';
import {
  ChevronLeftMediumIcon,
  ChevronRightMediumIcon,
  CloseMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Carousel, CarouselRef } from '@components/Carousel';
import { ContentSlide } from '@components/PresentationRebrand/ContentSlide';
import { SlideId } from '@components/PresentationRebrand/constants';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

import {
  CallToActionSlide,
  CallToActionSlideButton,
} from './CallToActionSlide';
import { ImageSlide } from './ImageSlide';
import { VideoSlide } from './VideoSlide';
import './styles.scss';

export type SlideType = 'callToAction' | 'image' | 'video' | 'content';

export interface Slide {
  id: string | SlideId;
  type: SlideType;
  colorScheme?: ColorScheme;
  /**
   * @deprecated - use children prop instead.
   */
  content?: ReactNode;
  imageUrl?: string;
  desktopImageUrl?: string;
  mobileImageUrl?: string;
  videoUrl?: string;
  vimeoId?: string;
  vimeoHash?: string;
  videoHeadline?: string;
  videoSubheadline?: string;
  termsText?: string;
  heading?: string;
  buttons?: CallToActionSlideButton[];
  children?: ReactNode;
}

export type ColorScheme = 'purple' | 'cream';

export interface PresentationRebrandProps {
  slides: Slide[];
  showDivider?: boolean;
  onClose?: () => void;
  className?: string;
}

const PresentationRebrand: FC<PresentationRebrandProps> = ({
  slides,
  showDivider = false,
  onClose,
  className,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<CarouselRef>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { appointment, canUpdateState } = useAppointmentContext();
  const snackbar = useSnackbar();

  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === slides.length - 1;

  const handlePrevious = useCallback(() => {
    if (canUpdateState) {
      carouselRef.current?.slickPrev();
    } else {
      snackbar({
        message: `${appointment?.controller.name} has screen control`,
        variant: SnackbarVariant.Warning,
        preventDuplicate: true,
      });
    }
  }, [appointment?.controller.name, canUpdateState, snackbar]);

  const handleNext = useCallback(() => {
    if (canUpdateState) {
      carouselRef.current?.slickNext();
    } else {
      snackbar({
        message: `${appointment?.controller.name} has screen control`,
        variant: SnackbarVariant.Warning,
        preventDuplicate: true,
      });
    }
  }, [appointment?.controller.name, canUpdateState, snackbar]);

  useEffect(() => {
    const onKeyPress = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      if (event.key === 'ArrowLeft' && canUpdateState && !isFirstSlide) {
        handlePrevious();
      }

      if (event.key === 'ArrowRight' && canUpdateState && !isLastSlide) {
        handleNext();
      }
    };

    window.addEventListener('keydown', onKeyPress);

    return () => window.removeEventListener('keydown', onKeyPress);
  }, [canUpdateState, handleNext, handlePrevious, isFirstSlide, isLastSlide]);

  const handleSlideChange = (index: number) => {
    carouselRef.current?.slickGoTo(index);
  };

  const renderSlideContent = (slide: Slide, index: number) => {
    switch (slide.type) {
      case 'image': {
        const desktop = slide.desktopImageUrl || slide.imageUrl;
        const mobile = slide.mobileImageUrl || slide.imageUrl || desktop;

        if (!desktop) return null;

        return (
          <ImageSlide desktopImageUrl={desktop} mobileImageUrl={mobile ?? ''} />
        );
      }
      case 'video': {
        return slide.vimeoId ? (
          <VideoSlide
            vimeoId={slide.vimeoId}
            vimeoHash={slide.vimeoHash}
            headline={slide.videoHeadline}
            subheadline={slide.videoSubheadline}
            isActive={index === currentSlide}
          />
        ) : (
          <div className="presentation-rebrand__slide-video">
            <video src={slide.videoUrl} controls />
          </div>
        );
      }
      case 'callToAction': {
        return slide.heading && slide.buttons ? (
          <CallToActionSlide
            heading={slide.heading}
            buttons={slide.buttons}
            onSlideChange={handleSlideChange}
            onQuoteClick={onClose}
            canUpdateState={canUpdateState}
          />
        ) : (
          <div className="presentation-rebrand__slide-content">
            {slide.content}
          </div>
        );
      }
      case 'content': {
        return <ContentSlide children={slide.children} />;
      }
      default: {
        return (
          <div className="presentation-rebrand__slide-content">
            {slide.content}
          </div>
        );
      }
    }
  };

  const currentSlideData = slides[currentSlide];
  const currentSlideColorScheme = currentSlideData?.colorScheme || 'cream';
  const shouldShowDivider = showDivider && currentSlideData?.type === 'image';

  return (
    <Flex
      direction="column"
      className={clsx(
        'presentation-rebrand',
        `presentation-rebrand--${currentSlideColorScheme}`,
        { 'presentation-rebrand--with-divider': shouldShowDivider },
        {
          'presentation-rebrand--image-slide':
            currentSlideData?.type === 'image',
        },
        {
          'presentation-rebrand--content-slide':
            currentSlideData?.type === 'content',
        },
        className
      )}
    >
      {onClose && canUpdateState && (
        <Button
          className="presentation-rebrand__close"
          onClick={onClose}
          aria-label="Close"
          variant="emphasis"
          data-testid="presentation-close"
        >
          <CloseMediumIcon />
        </Button>
      )}

      <Flex
        direction="column"
        className="presentation-rebrand__content"
        ref={contentRef}
      >
        <Carousel
          ref={carouselRef}
          id="presentation-carousel"
          settings={{
            adaptiveHeight: true,
            arrows: false,
            beforeChange: (_current: number, next: number) => {
              setCurrentSlide(next);
              window.scrollTo({ top: 0, behavior: 'instant' });
              document
                .querySelectorAll('.image-slide, .slick-list, .slick-slide')
                .forEach((el) =>
                  (el as HTMLElement).scrollTo({ top: 0, behavior: 'instant' })
                );
            },
            swipe: canUpdateState,
          }}
        >
          {slides.map((slide, index) => (
            <Flex key={slide.id} direction="column" className="carousel__Slide">
              {renderSlideContent(slide, index)}
            </Flex>
          ))}
        </Carousel>
      </Flex>

      {shouldShowDivider && (
        <Flex justifyContent="center">
          <div className="presentation-rebrand__divider" />
        </Flex>
      )}

      <Flex justifyContent="center">
        <Flex className="presentation-rebrand__footer">
          <Flex className="presentation-rebrand__terms">
            {currentSlideData?.termsText && (
              <BodyText>{currentSlideData.termsText}</BodyText>
            )}
          </Flex>

          <Flex className="presentation-rebrand__navigation">
            {!isFirstSlide && canUpdateState && (
              <Button
                onClick={handlePrevious}
                aria-label="Previous slide"
                variant="emphasis"
              >
                <ChevronLeftMediumIcon />
              </Button>
            )}
            {!isLastSlide && canUpdateState && (
              <Button
                onClick={handleNext}
                aria-label="Next slide"
                variant="emphasis"
              >
                <ChevronRightMediumIcon />
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export { PresentationRebrand };
